'use client';

import * as React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/** Maximum fine per kategori, from KUHP (UU 1/2023) Pasal 79 ayat (1). */
const KATEGORI_DENDA: Record<string, { huruf: string; amount: string }> = {
  I: { huruf: 'a', amount: 'Rp1.000.000,00 (satu juta rupiah)' },
  II: { huruf: 'b', amount: 'Rp10.000.000,00 (sepuluh juta rupiah)' },
  III: { huruf: 'c', amount: 'Rp50.000.000,00 (lima puluh juta rupiah)' },
  IV: { huruf: 'd', amount: 'Rp200.000.000,00 (dua ratus juta rupiah)' },
  V: { huruf: 'e', amount: 'Rp500.000.000,00 (lima ratus juta rupiah)' },
  VI: { huruf: 'f', amount: 'Rp2.000.000.000,00 (dua miliar rupiah)' },
  VII: { huruf: 'g', amount: 'Rp5.000.000.000,00 (lima miliar rupiah)' },
  VIII: { huruf: 'h', amount: 'Rp50.000.000.000,00 (lima puluh miliar rupiah)' },
};

/** "kategori VI"; longer numerals first so "VI" is not read as "V". */
const KATEGORI_PATTERN = /(kategori\s+(VIII|VII|VI|IV|V|III|II|I))\b/gi;

/**
 * Renders legal text with every fine category ("kategori VI") shown as a term
 * that explains, on hover or focus, the maximum fine it stands for under KUHP
 * Pasal 79. Readers of a consolidated article otherwise see only the category.
 *
 * Callers wrap this in a Radix TooltipProvider.
 *
 * @param text The text to render.
 * @param renderText Renders a run of text without a category (e.g. with search highlighting).
 */
export function KategoriDendaText({
  text,
  renderText,
}: {
  text: string;
  renderText: (part: string, key: string) => React.ReactNode;
}) {
  // split() with two capture groups yields [text, full match, numeral, text, ...].
  const parts = text.split(KATEGORI_PATTERN);
  if (parts.length === 1) {
    return <>{renderText(text, 'text-0')}</>;
  }
  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) {
      nodes.push(renderText(parts[i], `text-${i}`));
    }
    const term = parts[i + 1];
    if (term) {
      nodes.push(<KategoriDendaTerm key={`kategori-${i}`} term={term} numeral={parts[i + 2].toUpperCase()} />);
    }
  }
  return <>{nodes}</>;
}

/** One "kategori N" term with its KUHP Pasal 79 amount in a tooltip. */
function KategoriDendaTerm({ term, numeral }: { term: string; numeral: string }) {
  const denda = KATEGORI_DENDA[numeral];
  if (!denda) {
    return <>{term}</>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="cursor-help text-blue-600 underline decoration-dotted decoration-blue-400 underline-offset-2 hover:text-blue-800"
        >
          {term}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="font-semibold">Kategori {numeral}</p>
        <p>Pidana denda paling banyak {denda.amount}</p>
        <p className="mt-1 opacity-80">Pasal 79 ayat (1) huruf {denda.huruf} KUHP (UU 1/2023)</p>
      </TooltipContent>
    </Tooltip>
  );
}
